// Die Korrektur-Dialoge. Jeder ist rein: er sammelt eine Eingabe, prüft sie mit dem geteilten zod-Schema des
// Endpunkts und reicht den fertigen Körper nach oben. Er ruft nichts, er kennt keine tenantId, er weiß nichts
// von Warteschlangen — wer ihn öffnet, entscheidet, was mit dem Körper geschieht. Deshalb benutzen ihn die
// Prüfliste (Task 88), der Zahlungen-Bildschirm (Task 90) und die Provisionen (Task 91) unverändert.
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, type ReactNode } from 'react'
import { useForm, type FieldValues, type Path, type UseFormSetError } from 'react-hook-form'
import { z } from 'zod'
import {
  abschlussPatchSchema, createDealSchema, DEAL_STATUSES, dismissSchema, manualPaymentSchema, refundDecisionSchema,
  zahlungPatchSchema,
  type AbschlussPatch, type CreateDeal, type Dismiss, type ManualPayment, type RefundDecision, type ZahlungPatch,
} from '../../shared/schemas/corrections'
import type { AbschlussZeile } from '../api'
import { datum } from '../format'
import { centsAusEingabe, eingabeAusCents } from '../geld'
import { Betrag } from './Betrag'
import { Dialog } from './Dialog'
import { Feld } from './Feld'
import { Knopf } from './Knopf'

// ---------------------------------------------------------------------------------------------------
// Rahmen
// ---------------------------------------------------------------------------------------------------

export interface RahmenProps {
  offen: boolean
  laeuft: boolean
  /** Der deutsche `message` des Servers, wörtlich — die Oberfläche erfindet zu einem Serverfehler nichts. */
  fehler: string | null
  onSchliessen: () => void
}

interface KorrekturrahmenProps extends RahmenProps {
  titel: string
  erklaerung: ReactNode
  absendenText: string
  onAbsenden: () => void
  children: ReactNode
}

/**
 * Der Rahmen zeigt genau einen Fehler: den des Servers. Jede Beanstandung, die im Client entsteht, hängt
 * über `setError` an ihrem Feld und wird von `<Feld>` neben der Beschriftung angesagt — einmal, nicht zweimal.
 * (Zwei `role="alert"` mit demselben Satz liest eine Vorlesesoftware zweimal vor.)
 */
function Korrekturrahmen(p: KorrekturrahmenProps): ReactNode {
  return (
    <Dialog offen={p.offen} titel={p.titel} onSchliessen={p.onSchliessen}>
      <form
        onSubmit={(ereignis) => {
          ereignis.preventDefault()
          p.onAbsenden()
        }}
      >
        <div className="erklaerung">{p.erklaerung}</div>
        {p.children}
        {p.fehler === null ? null : <p role="alert" className="fehler">{p.fehler}</p>}
        <div className="knoepfe">
          <Knopf type="submit" variante="primaer" disabled={p.laeuft}>
            {p.laeuft ? 'Bitte warten …' : p.absendenText}
          </Knopf>
          <Knopf type="button" variante="still" onClick={p.onSchliessen} disabled={p.laeuft}>Abbrechen</Knopf>
        </div>
      </form>
    </Dialog>
  )
}

/** Der Grund steht in jedem Korrektur-Körper und ist überall Pflicht (schemas/corrections.ts). */
export const grundSchema = z.string().trim().min(3, { message: 'Bitte einen Grund angeben (mindestens 3 Zeichen).' }).max(500)

/** Eine Beanstandung des Schemas: die deutsche Meldung und — wenn zod eine nennt — das Körperfeld, das sie meint. */
export interface Beanstandung {
  koerperfeld: string | undefined
  meldung: string
}

/**
 * Der letzte Prüfschritt vor dem Absenden: der fertige Körper gegen genau das Schema, mit dem der Server ihn
 * gleich prüfen wird. Gibt das erste Problem zurück, oder null.
 */
export function pruefeKoerper<T>(schema: z.ZodType<T>, koerper: unknown): Beanstandung | null {
  const ergebnis = schema.safeParse(koerper)
  if (ergebnis.success) return null
  const problem = ergebnis.error.issues[0]
  const stelle = problem?.path[0]
  return {
    koerperfeld: typeof stelle === 'string' ? stelle : undefined,
    meldung: problem?.message ?? 'Die Eingabe ist unvollständig oder ungültig.',
  }
}

/**
 * Hängt eine Beanstandung an das Formularfeld, das sie meint. `felder` übersetzt die Körperfelder in die
 * Feldnamen der Ansicht (Euro-Feld statt Cent-Feld, ein Rollenfeld statt dreier); was dort nicht steht — eine
 * Beanstandung an `version` etwa, die gar kein Feld hat — landet am `auffang`. Ohne das stünde die Klage über
 * den Rechnungsnamen unter „Kumuliert bezahlt (€)".
 */
export function melde<F extends FieldValues>(
  setError: UseFormSetError<F>,
  felder: Partial<Record<string, Path<F>>>,
  auffang: Path<F>,
  b: Beanstandung,
): void {
  const ziel = b.koerperfeld === undefined ? undefined : felder[b.koerperfeld]
  setError(ziel ?? auffang, { message: b.meldung })
}

// ---------------------------------------------------------------------------------------------------
// Zahlung: Produkt zuweisen
// ---------------------------------------------------------------------------------------------------

export interface ProduktOption { id: string; code: string; name: string }

export interface ProduktZuweisenProps extends RahmenProps {
  erklaerung: ReactNode
  produkte: ProduktOption[]
  vorgabe: string | null
  onAbsenden: (koerper: ZahlungPatch) => void
}

interface ProduktAnsicht { manual_product_id: string; reason: string }

export function ProduktZuweisenDialog(p: ProduktZuweisenProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<ProduktAnsicht>({
    resolver: zodResolver(z.object({ manual_product_id: z.string(), reason: grundSchema })),
    defaultValues: { manual_product_id: p.vorgabe ?? '', reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Produkt zuweisen"
      absendenText="Produkt zuweisen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          p.onAbsenden({
            manual_product_id: werte.manual_product_id === '' ? null : werte.manual_product_id,
            reason: werte.reason,
          })
        })()
      }}
    >
      <Feld
        label="Produkt"
        htmlFor="k-produkt"
        hinweis="Das Produkt bestimmt, welche Provisionsregeln greifen. Leer lassen hebt eine bestehende Zuweisung auf."
      >
        <select id="k-produkt" {...register('manual_product_id')}>
          <option value="">— kein Produkt —</option>
          {p.produkte.map((prod) => (
            <option key={prod.id} value={prod.id}>{prod.code} — {prod.name}</option>
          ))}
        </select>
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-produkt-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-produkt-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Zahlung: Abschluss zuweisen
// ---------------------------------------------------------------------------------------------------

export interface AbschlussOption { id: string; bezeichnung: string }

export interface AbschlussZuweisenProps extends RahmenProps {
  erklaerung: ReactNode
  abschluesse: AbschlussOption[]
  vorgabe: string | null
  onAbsenden: (koerper: ZahlungPatch) => void
}

interface AbschlussAnsicht { manual_deal_id: string; reason: string }

export function AbschlussZuweisenDialog(p: AbschlussZuweisenProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<AbschlussAnsicht>({
    resolver: zodResolver(z.object({ manual_deal_id: z.string(), reason: grundSchema })),
    defaultValues: { manual_deal_id: p.vorgabe ?? '', reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Abschluss zuweisen"
      absendenText="Abschluss zuweisen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          p.onAbsenden({
            manual_deal_id: werte.manual_deal_id === '' ? null : werte.manual_deal_id,
            reason: werte.reason,
          })
        })()
      }}
    >
      <Feld
        label="Abschluss"
        htmlFor="k-abschluss"
        hinweis="Eine manuelle Zuordnung bleibt bestehen: spätere automatische Läufe rühren sie nicht an. Die Regeln benutzen dann das Produkt des Abschlusses."
      >
        <select id="k-abschluss" {...register('manual_deal_id')}>
          <option value="">— keine Zuordnung —</option>
          {p.abschluesse.map((a) => (
            <option key={a.id} value={a.id}>{a.bezeichnung}</option>
          ))}
        </select>
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-abschluss-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-abschluss-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Zahlung: ignorieren
// ---------------------------------------------------------------------------------------------------

export interface IgnorierenProps extends RahmenProps {
  erklaerung: ReactNode
  /** Gesetzt, wenn die Zahlung bereits ignoriert ist — dann hebt der Dialog die Ignorierung auf. */
  bereitsIgnoriert: boolean
  onAbsenden: (koerper: ZahlungPatch) => void
}

interface IgnorierenAnsicht { ignored_reason: string; reason: string }

export function ZahlungIgnorierenDialog(p: IgnorierenProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<IgnorierenAnsicht>({
    resolver: zodResolver(
      z.object({
        ignored_reason: p.bereitsIgnoriert
          ? z.string()
          : z.string().trim().min(1, { message: 'Bitte angeben, warum diese Zahlung ignoriert wird.' }),
        reason: grundSchema,
      }),
    ),
    defaultValues: { ignored_reason: '', reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel={p.bereitsIgnoriert ? 'Ignorierung aufheben' : 'Zahlung ignorieren'}
      absendenText={p.bereitsIgnoriert ? 'Ignorierung aufheben' : 'Zahlung ignorieren'}
      onAbsenden={() => {
        void handleSubmit((werte) => {
          p.onAbsenden({
            ignored_reason: p.bereitsIgnoriert ? null : werte.ignored_reason,
            reason: werte.reason,
          })
        })()
      }}
    >
      {p.bereitsIgnoriert ? null : (
        <Feld
          label="Vermerk an der Zahlung"
          htmlFor="k-ignoriert"
          fehler={formState.errors.ignored_reason?.message}
          hinweis="Eine ignorierte Zahlung erzeugt keine Provision und taucht in der Prüfliste nicht mehr auf. Der Vermerk bleibt an der Zahlung stehen."
        >
          <input id="k-ignoriert" type="text" {...register('ignored_reason')} />
        </Feld>
      )}
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-ignoriert-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-ignoriert-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Rate: Bezahlt am setzen
// ---------------------------------------------------------------------------------------------------

export interface DatumSetzenProps extends RahmenProps {
  erklaerung: ReactNode
  deltaId: string
  onAbsenden: (koerper: ZahlungPatch) => void
}

interface DatumAnsicht { delta_id: string; paid_at: string; reason: string }

export function DatumSetzenDialog(p: DatumSetzenProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<DatumAnsicht>({
    resolver: zodResolver(
      z.object({
        delta_id: z.string(),
        paid_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Bitte ein Datum im Format JJJJ-MM-TT angeben.' }),
        reason: grundSchema,
      }),
    ),
    defaultValues: { delta_id: p.deltaId, paid_at: '', reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Bezahlt am setzen"
      absendenText="Datum setzen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          p.onAbsenden({ delta_id: werte.delta_id, paid_at: werte.paid_at, reason: werte.reason })
        })()
      }}
    >
      <Feld
        label="Bezahlt am"
        htmlFor="k-datum"
        fehler={formState.errors.paid_at?.message}
        hinweis="Der Tag, an dem das Geld beim Auftraggeber eingegangen ist. Er wird auf der Rechnung des Auftragnehmers als Leistungsdatum gedruckt."
      >
        <input id="k-datum" type="date" {...register('paid_at')} />
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-datum-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-datum-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Rate: Netto/Brutto korrigieren
// ---------------------------------------------------------------------------------------------------

export interface NettoBruttoProps extends RahmenProps {
  erklaerung: ReactNode
  deltaId: string
  /** Der bekannte Betrag in Cent, oder `null`, wenn keiner bekannt ist — dann bleibt das Feld leer. Nicht 0:
   *  eine echte Null ist ein Betrag und soll als „0,00" dastehen. */
  vorgabeNetto: number | null
  vorgabeBrutto: number | null
  onAbsenden: (koerper: ZahlungPatch) => void
}

interface NettoAnsicht { netto: string; brutto: string; reason: string }

/**
 * Der Titel dieses Dialogs sagt „Rechnungsbeträge", nicht „Netto und Brutto": über `aria-labelledby` ist er der
 * zugängliche Name des Dialogfensters, und eine Suche nach der Beschriftung „Netto" fände dann zwei Elemente —
 * das Fenster und das Feld. Die beiden Beträge tragen ihre Wörter dafür groß in der Feldbeschriftung.
 */
export function NettoBruttoDialog(p: NettoBruttoProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<NettoAnsicht>({
    resolver: zodResolver(z.object({ netto: z.string(), brutto: z.string(), reason: grundSchema })),
    defaultValues: {
      netto: p.vorgabeNetto === null ? '' : eingabeAusCents(p.vorgabeNetto),
      brutto: p.vorgabeBrutto === null ? '' : eingabeAusCents(p.vorgabeBrutto),
      reason: '',
    },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Rechnungsbeträge korrigieren"
      absendenText="Beträge korrigieren"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          const netto = centsAusEingabe(werte.netto)
          const brutto = centsAusEingabe(werte.brutto)
          if (netto === null) {
            setError('netto', { message: 'Bitte einen Betrag wie 4275,00 angeben.' })
            return
          }
          if (brutto === null) {
            setError('brutto', { message: 'Bitte einen Betrag wie 5087,25 angeben.' })
            return
          }
          const koerper = { delta_id: p.deltaId, net_cents: netto, gross_cents: brutto, reason: werte.reason }
          const problem = pruefeKoerper(zahlungPatchSchema, koerper)
          if (problem !== null) {
            melde(setError, { net_cents: 'netto', gross_cents: 'brutto', reason: 'reason' }, 'netto', problem)
            return
          }
          p.onAbsenden(koerper)
        })()
      }}
    >
      <Feld
        label="Netto der Kundenrechnung (€)"
        htmlFor="k-netto"
        fehler={formState.errors.netto?.message}
        hinweis="Netto und Brutto der Kundenrechnung. Aus ihnen bildet der Server die Provisionsbasis dieser Rate; die Oberfläche rechnet sie nicht aus."
      >
        <input id="k-netto" type="text" inputMode="decimal" {...register('netto')} />
      </Feld>
      <Feld label="Brutto der Kundenrechnung (€)" htmlFor="k-brutto" fehler={formState.errors.brutto?.message}>
        <input id="k-brutto" type="text" inputMode="decimal" {...register('brutto')} />
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-netto-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-netto-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Rückzahlung entscheiden
// ---------------------------------------------------------------------------------------------------

export interface RueckzahlungProps extends RahmenProps {
  erklaerung: ReactNode
  entscheidung: 'confirm' | 'ignore'
  onAbsenden: (koerper: RefundDecision) => void
}

export function RueckzahlungDialog(p: RueckzahlungProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<RefundDecision>({
    resolver: zodResolver(refundDecisionSchema),
    defaultValues: { reason: '' },
  })
  const bestaetigen = p.entscheidung === 'confirm'
  return (
    <Korrekturrahmen
      {...p}
      titel={bestaetigen ? 'Rückzahlung bestätigen' : 'Rückzahlung ignorieren'}
      absendenText={bestaetigen ? 'Rückzahlung bestätigen' : 'Rückzahlung ignorieren'}
      onAbsenden={() => { void handleSubmit((werte) => { p.onAbsenden(werte) })() }}
    >
      <p>
        {bestaetigen
          ? 'Der verbuchte Zahlungsstand dieser Rechnung wird um die Rückzahlung gesenkt. Provisionen, die auf dem zurückgezahlten Teil beruhen, werden vom Server zurückgebucht — bereits abgerechnete Zeilen als Rückbuchung auf der nächsten Abrechnung.'
          : 'Der verbuchte Zahlungsstand bleibt, wie er ist. Es wird nichts zurückgebucht; die Entscheidung und ihr Grund stehen im Protokoll.'}
      </p>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-refund-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-refund-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Abschluss aus einer Zahlung anlegen
// ---------------------------------------------------------------------------------------------------

export interface BeteiligterOption { id: string; bezeichnung: string }

export interface AbschlussAnlegenProps extends RahmenProps {
  erklaerung: ReactNode
  produkte: ProduktOption[]
  beteiligte: BeteiligterOption[]
  /** das Produkt, das die Zahlung schon trägt — dann ist nur noch der Closer zu wählen */
  vorgabeProduktId?: string | null
  onAbsenden: (koerper: CreateDeal) => void
}

interface AnlegenAnsicht {
  product_id: string
  closer_tc_id: string
  setter_tc_id: string
  host_tc_id: string
  sonstige_tc_id: string
  signed_at: string
  reason: string
}

export function AbschlussAnlegenDialog(p: AbschlussAnlegenProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<AnlegenAnsicht>({
    resolver: zodResolver(
      z.object({
        product_id: z.string().min(1, { message: 'Ohne Produkt greift keine Regel. Bitte ein Produkt wählen.' }),
        closer_tc_id: z.string(),
        setter_tc_id: z.string(),
        host_tc_id: z.string(),
        sonstige_tc_id: z.string(),
        signed_at: z.string(),
        reason: grundSchema,
      }),
    ),
    defaultValues: {
      product_id: p.vorgabeProduktId ?? '', closer_tc_id: '', setter_tc_id: '', host_tc_id: '', sonstige_tc_id: '', signed_at: '',
      // Ein Grund steht schon da: wer nur den Closer nachträgt, soll dafür keinen Satz tippen müssen.
      reason: 'Closer nachträglich zugeordnet',
    },
  })
  const leer = (wert: string): string | null => (wert === '' ? null : wert)
  return (
    <Korrekturrahmen
      {...p}
      titel="Closer zuordnen"
      absendenText="Zuordnen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          const koerper: CreateDeal = {
            product_id: werte.product_id,
            closer_tc_id: leer(werte.closer_tc_id),
            setter_tc_id: leer(werte.setter_tc_id),
            host_tc_id: leer(werte.host_tc_id),
            sonstige_tc_id: leer(werte.sonstige_tc_id),
            signed_at: leer(werte.signed_at),
            reason: werte.reason,
          }
          const problem = pruefeKoerper(createDealSchema, koerper)
          if (problem !== null) {
            melde(
              setError,
              {
                product_id: 'product_id', closer_tc_id: 'closer_tc_id', setter_tc_id: 'setter_tc_id',
                host_tc_id: 'host_tc_id', sonstige_tc_id: 'sonstige_tc_id', signed_at: 'signed_at', reason: 'reason',
              },
              'product_id',
              problem,
            )
            return
          }
          p.onAbsenden(koerper)
        })()
      }}
    >
      <p>
        Zu dieser Zahlung kam kein Abschluss. Wählen Sie, wer ihn geschlossen hat — daraus entsteht der Abschluss,
        und die Provision rechnet sofort. Kunde, Kontakt und Betrag kommen aus der Zahlung.
      </p>
      <Feld label="Closer" htmlFor="k-neu-closer" fehler={formState.errors.closer_tc_id?.message}>
        <select id="k-neu-closer" {...register('closer_tc_id')}>
          <option value="">— bitte wählen —</option>
          {p.beteiligte.map((b) => <option key={b.id} value={b.id}>{b.bezeichnung}</option>)}
        </select>
      </Feld>
      <Feld label="Produkt" htmlFor="k-neu-produkt" fehler={formState.errors.product_id?.message}>
        <select id="k-neu-produkt" {...register('product_id')}>
          <option value="">— bitte wählen —</option>
          {p.produkte.map((prod) => <option key={prod.id} value={prod.id}>{prod.code} — {prod.name}</option>)}
        </select>
      </Feld>
      <details>
        <summary>Weitere Rollen, Datum, Grund</summary>
      <Feld label="Setter" htmlFor="k-neu-setter" fehler={formState.errors.setter_tc_id?.message}>
        <select id="k-neu-setter" {...register('setter_tc_id')}>
          <option value="">— keiner —</option>
          {p.beteiligte.map((b) => <option key={b.id} value={b.id}>{b.bezeichnung}</option>)}
        </select>
      </Feld>
      <Feld label="Empfehlungsgeber" htmlFor="k-neu-host" fehler={formState.errors.host_tc_id?.message}>
        <select id="k-neu-host" {...register('host_tc_id')}>
          <option value="">— keiner —</option>
          {p.beteiligte.map((b) => <option key={b.id} value={b.id}>{b.bezeichnung}</option>)}
        </select>
      </Feld>
      <Feld label="Sonstige Rolle" htmlFor="k-neu-sonstige" fehler={formState.errors.sonstige_tc_id?.message}>
        <select id="k-neu-sonstige" {...register('sonstige_tc_id')}>
          <option value="">— keiner —</option>
          {p.beteiligte.map((b) => <option key={b.id} value={b.id}>{b.bezeichnung}</option>)}
        </select>
      </Feld>
      <Feld label="Abschluss am" htmlFor="k-neu-datum" fehler={formState.errors.signed_at?.message} hinweis="Ohne Datum zählt der Abschluss im Dashboard unter „ohne Datum“, nicht in einem Monat.">
        <input id="k-neu-datum" type="date" {...register('signed_at')} />
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-neu-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-neu-grund" rows={2} {...register('reason')} />
      </Feld>
      </details>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Zahlung von Hand erfassen
// ---------------------------------------------------------------------------------------------------

export interface ZahlungVonHandProps extends RahmenProps {
  erklaerung: ReactNode
  vorgabe: {
    invoice_number: string
    customer_name: string
    invoice_name: string
    hl_contact_id: string | null
    cumulative_paid_cents: number | null
    source_webhook_event_id: string | null
  }
  onAbsenden: (koerper: ManualPayment) => void
}

interface VonHandAnsicht {
  invoice_number: string
  customer_name: string
  invoice_name: string
  hl_contact_id: string
  paid_at: string
  betrag: string
  netto: string
  brutto: string
  reason: string
}

export function ZahlungVonHandDialog(p: ZahlungVonHandProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<VonHandAnsicht>({
    resolver: zodResolver(
      z.object({
        invoice_number: z.string().trim().min(1, { message: 'Die Zahlungs-ID aus FinSuites fehlt.' }),
        customer_name: z.string().trim().min(1, { message: 'Der Kundenname fehlt.' }),
        invoice_name: z.string(),
        hl_contact_id: z.string(),
        paid_at: z.string(),
        betrag: z.string(),
        netto: z.string(),
        brutto: z.string(),
        reason: grundSchema,
      }),
    ),
    defaultValues: {
      invoice_number: p.vorgabe.invoice_number,
      customer_name: p.vorgabe.customer_name,
      invoice_name: p.vorgabe.invoice_name,
      hl_contact_id: p.vorgabe.hl_contact_id ?? '',
      paid_at: '',
      betrag: p.vorgabe.cumulative_paid_cents === null ? '' : eingabeAusCents(p.vorgabe.cumulative_paid_cents),
      netto: '',
      brutto: '',
      reason: '',
    },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Zahlung von Hand erfassen"
      absendenText="Zahlung erfassen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          const betrag = centsAusEingabe(werte.betrag)
          if (betrag === null) {
            setError('betrag', { message: 'Bitte einen Betrag wie 4500,00 angeben.' })
            return
          }
          // `centsAusEingabe` gibt für ein leeres UND für ein unlesbares Feld `null`. Netto und Brutto sind
          // freiwillig — ein leeres Feld heißt „nicht angegeben", ein vertipptes darf aber nicht als „nicht
          // angegeben" durchrutschen: der Server bildete die Provisionsbasis dann selbst, statt aus der Zahl,
          // die hier auf dem Bildschirm steht.
          const netto = werte.netto.trim() === '' ? null : centsAusEingabe(werte.netto)
          if (netto === null && werte.netto.trim() !== '') {
            setError('netto', { message: 'Bitte einen Betrag wie 4275,00 angeben.' })
            return
          }
          const brutto = werte.brutto.trim() === '' ? null : centsAusEingabe(werte.brutto)
          if (brutto === null && werte.brutto.trim() !== '') {
            setError('brutto', { message: 'Bitte einen Betrag wie 5087,25 angeben.' })
            return
          }
          const koerper: ManualPayment = {
            invoice_number: werte.invoice_number,
            hl_contact_id: werte.hl_contact_id === '' ? null : werte.hl_contact_id,
            customer_name: werte.customer_name,
            invoice_name: werte.invoice_name,
            paid_at: werte.paid_at === '' ? null : werte.paid_at,
            cumulative_paid_cents: betrag,
            invoice_net_cents: netto,
            invoice_gross_cents: brutto,
            source_webhook_event_id: p.vorgabe.source_webhook_event_id,
            reason: werte.reason,
          }
          const problem = pruefeKoerper(manualPaymentSchema, koerper)
          if (problem !== null) {
            melde(
              setError,
              {
                invoice_number: 'invoice_number', customer_name: 'customer_name', invoice_name: 'invoice_name',
                hl_contact_id: 'hl_contact_id', paid_at: 'paid_at', cumulative_paid_cents: 'betrag',
                invoice_net_cents: 'netto', invoice_gross_cents: 'brutto', reason: 'reason',
              },
              'betrag',
              problem,
            )
            return
          }
          p.onAbsenden(koerper)
        })()
      }}
    >
      <p>
        Erfasst die Lieferung, die FinSuites hätte schicken sollen. Der Betrag ist der <strong>kumulierte</strong>
        {' '}Zahlungsstand dieser Rechnung, nicht die einzelne Rate — die Rate bildet der Server aus der Differenz
        zum bisher Verbuchten.
      </p>
      <Feld label="Zahlungs-ID (Rechnungsnummer in FinSuites)" htmlFor="k-hand-nr" fehler={formState.errors.invoice_number?.message}>
        <input id="k-hand-nr" type="text" {...register('invoice_number')} />
      </Feld>
      <Feld label="Kunde" htmlFor="k-hand-kunde" fehler={formState.errors.customer_name?.message}>
        <input id="k-hand-kunde" type="text" {...register('customer_name')} />
      </Feld>
      <Feld label="Rechnungsname" htmlFor="k-hand-name" fehler={formState.errors.invoice_name?.message} hinweis="Aus ihm erkennt der Server das Produkt.">
        <input id="k-hand-name" type="text" {...register('invoice_name')} />
      </Feld>
      <Feld label="Kontakt-ID" htmlFor="k-hand-kontakt" fehler={formState.errors.hl_contact_id?.message}>
        <input id="k-hand-kontakt" type="text" {...register('hl_contact_id')} />
      </Feld>
      <Feld label="Bezahlt am" htmlFor="k-hand-datum" fehler={formState.errors.paid_at?.message}>
        <input id="k-hand-datum" type="date" {...register('paid_at')} />
      </Feld>
      <Feld label="Kumuliert bezahlt (€)" htmlFor="k-hand-betrag" fehler={formState.errors.betrag?.message}>
        <input id="k-hand-betrag" type="text" inputMode="decimal" {...register('betrag')} />
      </Feld>
      <Feld label="Netto der Kundenrechnung (€)" htmlFor="k-hand-netto" fehler={formState.errors.netto?.message}>
        <input id="k-hand-netto" type="text" inputMode="decimal" {...register('netto')} />
      </Feld>
      <Feld label="Brutto der Kundenrechnung (€)" htmlFor="k-hand-brutto" fehler={formState.errors.brutto?.message}>
        <input id="k-hand-brutto" type="text" inputMode="decimal" {...register('brutto')} />
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-hand-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-hand-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Erledigt (Prüfzeile abhaken)
// ---------------------------------------------------------------------------------------------------

export interface ErledigenProps extends RahmenProps {
  erklaerung: ReactNode
  onAbsenden: (koerper: Dismiss) => void
}

export function ErledigenDialog(p: ErledigenProps): ReactNode {
  const { register, handleSubmit, formState } = useForm<Dismiss>({
    resolver: zodResolver(dismissSchema),
    defaultValues: { note: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Als erledigt abhaken"
      absendenText="Erledigt"
      onAbsenden={() => { void handleSubmit((werte) => { p.onAbsenden(werte) })() }}
    >
      <p>
        „Ich habe es gesehen, es bleibt so." Die Zeile verschwindet aus der Prüfliste und aus dem Kontroll-Zähler.
        Tritt dasselbe Problem an einem anderen Fall auf, steht es wieder hier.
      </p>
      <Feld label="Vermerk" htmlFor="k-erledigt-note" fehler={formState.errors.note?.message}>
        <textarea id="k-erledigt-note" rows={2} {...register('note')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------
// Abschluss: ein Feld ändern (versionsgesichert)
// ---------------------------------------------------------------------------------------------------

/** Deutsche Namen der sechs Abschluss-Status. Reiner Oberflächentext; die Werte kommen aus DEAL_STATUSES. */
export const STATUS_TEXT: Readonly<Record<(typeof DEAL_STATUSES)[number], string>> = {
  aktiv: 'aktiv',
  weiterlauf: 'Weiterlauf',
  beendet: 'beendet',
  storno: 'Storno',
  ignorieren: 'ignorieren',
  pruefen: 'prüfen',
}

export type RolleFeld = 'closer_tc_id' | 'setter_tc_id' | 'host_tc_id' | 'sonstige_tc_id'

export interface AbschlussBezug { id: string; version: number }

export interface RolleZuweisenProps extends RahmenProps {
  erklaerung: ReactNode
  feld: RolleFeld
  /** „Closer“, „Setter“, „Empfehlungsgeber“ — steht in der Überschrift und am Feld. */
  rolleName: string
  beteiligte: BeteiligterOption[]
  vorgabe: string | null
  abschluss: AbschlussBezug
  onAbsenden: (koerper: AbschlussPatch) => void
}

interface RolleAnsicht { empfaenger: string; reason: string }

export function RolleZuweisenDialog(p: RolleZuweisenProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<RolleAnsicht>({
    resolver: zodResolver(z.object({ empfaenger: z.string(), reason: grundSchema })),
    defaultValues: { empfaenger: p.vorgabe ?? '', reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel={`${p.rolleName} zuweisen`}
      absendenText={`${p.rolleName} zuweisen`}
      onAbsenden={() => {
        void handleSubmit((werte) => {
          const wert = werte.empfaenger === '' ? null : werte.empfaenger
          // Das Zielfeld steht erst zur Laufzeit fest; ausgeschrieben statt dynamisch, damit der Körper
          // typisiert bleibt und kein `as` nötig ist.
          const koerper: AbschlussPatch =
            p.feld === 'closer_tc_id'
              ? { version: p.abschluss.version, closer_tc_id: wert, reason: werte.reason }
              : p.feld === 'setter_tc_id'
                ? { version: p.abschluss.version, setter_tc_id: wert, reason: werte.reason }
                : p.feld === 'sonstige_tc_id'
                  ? { version: p.abschluss.version, sonstige_tc_id: wert, reason: werte.reason }
                  : { version: p.abschluss.version, host_tc_id: wert, reason: werte.reason }
          const problem = pruefeKoerper(abschlussPatchSchema, koerper)
          if (problem !== null) {
            // Alle drei Rollenfelder zeigen auf dasselbe Auswahlfeld — welches im Körper steht, entscheidet
            // `p.feld`; die Ansicht hat für alle drei nur `empfaenger`.
            melde(
              setError,
              { closer_tc_id: 'empfaenger', setter_tc_id: 'empfaenger', host_tc_id: 'empfaenger', sonstige_tc_id: 'empfaenger', reason: 'reason' },
              'empfaenger',
              problem,
            )
            return
          }
          p.onAbsenden(koerper)
        })()
      }}
    >
      <p>
        Die Rolle wird am Abschluss gesetzt, nicht an der einzelnen Provisionszeile. Der Server bewertet danach
        jede Zahlung dieses Abschlusses neu und ersetzt die Zeile ohne Empfänger.
      </p>
      <Feld label={p.rolleName} htmlFor="k-rolle" fehler={formState.errors.empfaenger?.message}>
        <select id="k-rolle" {...register('empfaenger')}>
          <option value="">— niemand —</option>
          {p.beteiligte.map((b) => <option key={b.id} value={b.id}>{b.bezeichnung}</option>)}
        </select>
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-rolle-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-rolle-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

export interface AbschlussProduktProps extends RahmenProps {
  erklaerung: ReactNode
  produkte: ProduktOption[]
  vorgabe: string | null
  abschluss: AbschlussBezug
  onAbsenden: (koerper: AbschlussPatch) => void
}

interface DealProduktAnsicht { product_id: string; reason: string }

export function AbschlussProduktDialog(p: AbschlussProduktProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<DealProduktAnsicht>({
    resolver: zodResolver(z.object({ product_id: z.string(), reason: grundSchema })),
    defaultValues: { product_id: p.vorgabe ?? '', reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Produkt am Abschluss setzen"
      absendenText="Produkt setzen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          const koerper: AbschlussPatch = {
            version: p.abschluss.version,
            product_id: werte.product_id === '' ? null : werte.product_id,
            reason: werte.reason,
          }
          const problem = pruefeKoerper(abschlussPatchSchema, koerper)
          if (problem !== null) {
            melde(setError, { product_id: 'product_id', reason: 'reason' }, 'product_id', problem)
            return
          }
          p.onAbsenden(koerper)
        })()
      }}
    >
      <p>Das Produkt des Abschlusses entscheidet, welche Regeln greifen — nicht das Produkt der Rechnung.</p>
      <Feld label="Produkt" htmlFor="k-dealprodukt" fehler={formState.errors.product_id?.message}>
        <select id="k-dealprodukt" {...register('product_id')}>
          <option value="">— kein Produkt —</option>
          {p.produkte.map((prod) => <option key={prod.id} value={prod.id}>{prod.code} — {prod.name}</option>)}
        </select>
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-dealprodukt-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-dealprodukt-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

export interface AbschlussStatusProps extends RahmenProps {
  erklaerung: ReactNode
  vorgabe: (typeof DEAL_STATUSES)[number]
  abschluss: AbschlussBezug
  onAbsenden: (koerper: AbschlussPatch) => void
}

interface StatusAnsicht { status: (typeof DEAL_STATUSES)[number]; reason: string }

export function AbschlussStatusDialog(p: AbschlussStatusProps): ReactNode {
  const { register, handleSubmit, formState, setError } = useForm<StatusAnsicht>({
    resolver: zodResolver(z.object({ status: z.enum(DEAL_STATUSES), reason: grundSchema })),
    defaultValues: { status: p.vorgabe, reason: '' },
  })
  return (
    <Korrekturrahmen
      {...p}
      titel="Status des Abschlusses ändern"
      absendenText="Status setzen"
      onAbsenden={() => {
        void handleSubmit((werte) => {
          const koerper: AbschlussPatch = { version: p.abschluss.version, status: werte.status, reason: werte.reason }
          const problem = pruefeKoerper(abschlussPatchSchema, koerper)
          if (problem !== null) {
            melde(setError, { status: 'status', reason: 'reason' }, 'status', problem)
            return
          }
          p.onAbsenden(koerper)
        })()
      }}
    >
      <p>
        Nur „aktiv“ und „Weiterlauf“ nehmen neue Zahlungen an. „Storno“ und „ignorieren“ nehmen den Abschluss
        außerdem aus dem Auftragsvolumen.
      </p>
      <Feld label="Status" htmlFor="k-dealstatus" fehler={formState.errors.status?.message}>
        <select id="k-dealstatus" {...register('status')}>
          {DEAL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_TEXT[s]}</option>)}
        </select>
      </Feld>
      <Feld label="Grund (fürs Protokoll)" htmlFor="k-dealstatus-grund" fehler={formState.errors.reason?.message}>
        <textarea id="k-dealstatus-grund" rows={2} {...register('reason')} />
      </Feld>
    </Korrekturrahmen>
  )
}

// ---------------------------------------------------------------------------------------------------------------------
// Doppelter Abschluss — vergleichen und entscheiden (Bastian, 11.10.2026)
// ---------------------------------------------------------------------------------------------------------------------

/** Was der Vergleich entscheidet: einen behalten (die anderen streichen) oder alle behalten. */
export type Vergleichsentscheidung =
  | { art: 'behalten'; behalten: AbschlussZeile; streichen: AbschlussZeile[]; grund: string }
  | { art: 'alle'; grund: string }

interface VergleichProps extends RahmenProps {
  erklaerung: ReactNode
  /** Die Abschlüsse mit demselben Kontakt und Produkt; der erste ist der der Prüfzeile. */
  abschluesse: AbschlussZeile[]
  /** Namen der Beteiligten (tenant_contractor_id → Anzeigename); fehlt einer, steht das Kürzel. */
  namen?: ReadonlyMap<string, string>
  onAbsenden: (entscheidung: Vergleichsentscheidung) => void
}

const LEAD_TEXT: Readonly<Record<'intern' | 'extern', string>> = { intern: 'von uns gestellt', extern: 'Empfehlung / extern' }

/** Die Zeilen des Vergleichs: Beschriftung und wie der Wert eines Abschlusses aussieht (Text zum Vergleichen, Anzeige). */
type Namen = ReadonlyMap<string, string>
const person = (id: string | null, kuerzel: string | null, namen: Namen): string =>
  id === null ? '—' : namen.get(id) ?? kuerzel ?? '—'

const VERGLEICHSZEILEN: ReadonlyArray<{ label: string; wert: (a: AbschlussZeile, namen: Namen) => string; anzeige?: (a: AbschlussZeile) => ReactNode }> = [
  { label: 'Abschluss-ID', wert: (a) => a.hl_deal_key ?? '—' },
  { label: 'Kunde', wert: (a) => a.customer_name },
  { label: 'Produkt', wert: (a) => a.product_name ?? a.product_code ?? '—' },
  { label: 'Unterschrieben am', wert: (a) => datum(a.signed_at) },
  {
    label: 'Vertragswert',
    wert: (a) => String(a.contract_value_cents ?? ''),
    anzeige: (a) => (a.contract_value_cents === null ? '—' : <Betrag cents={a.contract_value_cents} />),
  },
  { label: 'Closer', wert: (a, n) => person(a.closer_tc_id, a.closer_short_code, n) },
  { label: 'Setter', wert: (a, n) => person(a.setter_tc_id, a.setter_short_code, n) },
  { label: 'Empfehlungsgeber', wert: (a, n) => person(a.host_tc_id, a.host_short_code, n) },
  { label: 'Lead-Quelle', wert: (a) => (a.lead_source === null ? 'intern (ohne Vermerk)' : LEAD_TEXT[a.lead_source]) },
  { label: 'Status', wert: (a) => a.status },
  { label: 'Notiz', wert: (a) => a.notes ?? '—' },
]

export function AbschluesseVergleichenDialog(p: VergleichProps): ReactNode {
  const [wahl, setWahl] = useState<string>(p.abschluesse[0]?.id ?? 'alle')
  const [grund, setGrund] = useState('')
  const [grundFehler, setGrundFehler] = useState<string | undefined>(undefined)
  const name = (a: AbschlussZeile, i: number): string => a.hl_deal_key ?? `Abschluss ${i + 1}`
  const behalten = p.abschluesse.find((a) => a.id === wahl) ?? null
  const namen: Namen = p.namen ?? new Map()

  return (
    <Korrekturrahmen
      {...p}
      titel="Doppelten Abschluss prüfen"
      absendenText={behalten === null ? 'Alle behalten' : 'Zusammenführen'}
      onAbsenden={() => {
        const geprueft = grundSchema.safeParse(grund)
        if (!geprueft.success) {
          setGrundFehler(geprueft.error.issues[0]?.message)
          return
        }
        setGrundFehler(undefined)
        p.onAbsenden(behalten === null
          ? { art: 'alle', grund: geprueft.data }
          : { art: 'behalten', behalten, streichen: p.abschluesse.filter((a) => a.id !== behalten.id), grund: geprueft.data })
      }}
    >
      <div className="vergleich">
        <table className="tabelle vergleich-tabelle" aria-label="Abschlüsse im Vergleich">
          <thead>
            <tr>
              <th scope="col"><span className="nur-vorlesen">Angabe</span></th>
              {p.abschluesse.map((a, i) => <th key={a.id} scope="col">{name(a, i)}</th>)}
            </tr>
          </thead>
          <tbody>
            {VERGLEICHSZEILEN.map((zeile) => {
              const werte = p.abschluesse.map((a) => zeile.wert(a, namen))
              const abweichend = new Set(werte).size > 1
              return (
                <tr key={zeile.label} data-abweichend={abweichend ? 'true' : undefined}>
                  <th scope="row">
                    {zeile.label}
                    {abweichend ? <>{' '}<span className="nur-vorlesen">(unterschiedlich)</span></> : null}
                  </th>
                  {p.abschluesse.map((a) => <td key={a.id}>{zeile.anzeige === undefined ? zeile.wert(a, namen) : zeile.anzeige(a)}</td>)}
                </tr>
              )
            })}
          </tbody>
        </table>
        <p className="hinweis">Farbig markiert ist, worin sich die Abschlüsse unterscheiden.</p>
      </div>

      <fieldset className="entscheidung">
        <legend>Was stimmt?</legend>
        {p.abschluesse.map((a, i) => (
          <label key={a.id} className="entscheidung-wahl">
            <input type="radio" name="vergleich-wahl" value={a.id} checked={wahl === a.id} onChange={() => { setWahl(a.id) }} />
            <span>
              <strong>Derselbe Abschluss — {name(a, i)} behalten</strong>
              <span className="leise">
                Der andere wird gestrichen (Status „ignorieren“); seine Zahlungen rechnen danach auf {name(a, i)}.
              </span>
            </span>
          </label>
        ))}
        <label className="entscheidung-wahl">
          <input type="radio" name="vergleich-wahl" value="alle" checked={wahl === 'alle'} onChange={() => { setWahl('alle') }} />
          <span>
            <strong>Verschiedene Abschlüsse — alle behalten</strong>
            <span className="leise">
              Der Hinweis wird abgehakt. Neue Zahlungen dieses Kunden für dieses Produkt weist du dann je Zahlung zu.
            </span>
          </span>
        </label>
      </fieldset>

      <Feld label="Grund (fürs Protokoll)" htmlFor="k-vergleich-grund" fehler={grundFehler}>
        <textarea id="k-vergleich-grund" rows={2} value={grund} onChange={(e) => { setGrund(e.target.value) }} />
      </Feld>
    </Korrekturrahmen>
  )
}
